import { enumDeploymentUnitStatusV2 } from '@ingenico/api-sdk-device-management-private-v2';
import type { BusinessServiceRequest } from '@ingenico/react-section-utils';
import { useCurrentUser } from '@ingenico/react-section-utils';
import type { DeviceChangeResourceApiByIdV2 } from '@ingenico/react-ui';
import { useUserDateTimeFormat } from '@ingenico/react-utils/hooks';
import { toString } from 'lodash-es';
import { useMemo } from 'react';
import type { TFunction } from 'react-i18next';
import { dashString } from '../../utils/dashString.util';
import { useDeviceManagementError } from '../../hooks';
import type { ErrorWithPlaceholdersApiInData } from '../../utils/useDeviceManagementError.util';

// Similar implementation with sections/provisioning/src/lib/pages/provisioning-device-change-details-page/useProvisioningDeviceChangeDetailsPage.vm.tsx ;
/** Raise a ticket to try to refactor this part, and merge similarities */

export function useGenerateTargetDeviceIdItems(
  t: TFunction,
  targetDeviceByIdQuery: BusinessServiceRequest<DeviceChangeResourceApiByIdV2>,
) {
  const { getErrorMessage } = useDeviceManagementError();
  const errorAPI: ErrorWithPlaceholdersApiInData = useMemo(
    () => ({
      data: {
        type: toString(targetDeviceByIdQuery?.data?.statusDetail?.errorCode),
        placeholders: targetDeviceByIdQuery?.data?.statusDetail?.placeholders,
        title: toString(targetDeviceByIdQuery?.data?.description),
      },
    }),
    [targetDeviceByIdQuery],
  );
  if (targetDeviceByIdQuery?.data?.status !== enumDeploymentUnitStatusV2.FAILED) {
    return {
      subTitle: '',
      title: undefined,
      info: undefined,
    };
  }

  return {
    subTitle: t('field.deploymentFailed'),
    error: targetDeviceByIdQuery?.data?.statusDetail
      ? toString(getErrorMessage(errorAPI, t('drawer.emptyStatusDetails')))
      : toString(t('drawer.emptyStatusDetails')),
    info: targetDeviceByIdQuery?.data?.statusDetail
      ? toString(getErrorMessage(errorAPI, t('drawer.emptyStatusDetails')))
      : toString(t('drawer.emptyStatusDetails')),
  };
}

export function useGenerateTargetDeviceGeneralItems(
  t: TFunction,
  targetDeviceByIdQuery: BusinessServiceRequest<DeviceChangeResourceApiByIdV2>,
) {
  const currentUser = useCurrentUser();
  const { getUserDateTimeFormat } = useUserDateTimeFormat(currentUser.dateTimeFormat);
  return [
    {
      subTitle: t('field.model'),
      value: dashString(targetDeviceByIdQuery?.data?.physicalDevice?.model),
      halfSizeField: true,
    },
    {
      subTitle: t('field.manufacturer'),
      value: dashString(targetDeviceByIdQuery?.data?.physicalDevice?.manufacturer),
      halfSizeField: true,
    },
    {
      subTitle: t('field.serialNumber'),
      value: dashString(targetDeviceByIdQuery?.data?.physicalDevice?.serialNumber),
      halfSizeField: true,
    },
    {
      subTitle: t('field.partNumber'),
      value: dashString(targetDeviceByIdQuery?.data?.physicalDevice?.partNumber),
      halfSizeField: true,
    },
    {
      subTitle: t('field.startDate'),
      value: dashString(getUserDateTimeFormat({ instant: toString(targetDeviceByIdQuery?.data?.startDate) })),
      halfSizeField: true,
    },
    {
      subTitle: t('field.endDate'),
      value: dashString(getUserDateTimeFormat({ instant: toString(targetDeviceByIdQuery?.data?.endDate) })),
      halfSizeField: true,
    },
    {
      subTitle: t('field.deploymentRequest'),
      value: dashString(targetDeviceByIdQuery?.data?.name),
      halfSizeField: true,
      ...(targetDeviceByIdQuery?.data?.deploymentRequestId
        ? {
            link: {
              href: `../../../../device-management/deployments/${targetDeviceByIdQuery?.data?.deploymentRequestId}`,
            },
          }
        : {}),
    },
    {
      subTitle: t('field.configurationProfile'),
      value: dashString(targetDeviceByIdQuery?.data?.options?.configuration?.reference?.name),
      halfSizeField: true,
      ...(targetDeviceByIdQuery?.data?.options?.configuration?.reference?.name &&
      targetDeviceByIdQuery?.data?.options?.configuration?.reference?.id
        ? {
            link: {
              href: `../../../../device-management/configurations/${targetDeviceByIdQuery?.data?.options?.configuration?.reference?.id}`,
            },
          }
        : {}),
    },
  ];
}
